import { getLogger } from "@expert/logging";
import { Flex } from "@mantine/core";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo } from "react";
import { CustomerPanel } from "@/customer-panel";
import { Panels } from "@/panels";
import { getSessionByTaskId } from "@/sdk";
import { type ChatTask, useChatStore } from "@/sdk/agent/chat";
import { useWrapupTimer } from "@/sdk/sessions/hooks";
import { useSessionStore } from "@/sdk/sessions/session.store";
import { useFeatureFlag } from "@/shared-utils";
import { PlatformTimeline } from "@/timeline";
import { useSetupChatGaiaSubscriptions } from "@/timeline/state";
import { useChatExpertAssistEnabled } from "@/timeline/utils/chatExpertAssistFeatureFlag";
import ChatControlsBar from "./ChatControls/ChatControlsBar";
import { ChatPanelTimelineView } from "./ChatPanelTimelineView";
import { ChatQueue } from "./ChatQueue";

const logger = getLogger({
    rootModule: "ChatManager",
    supportTeam: "messaging",
});

/** provides a consistent layout for panel slots */
function SlotPanel({ children, ...props }: { children: ReactNode }) {
    return (
        <Flex h="100%" pos="absolute" style={{ containerType: "inline-size" }} inset={0} {...props}>
            {children}
        </Flex>
    );
}

export function ChatManager({ chatTasks }: { chatTasks: ChatTask[] }) {
    const activeChatId = useChatStore((state) => state.activeChatId);
    const setActiveChatId = useChatStore((state) => state.setActiveChatId);
    const clearActiveChatId = useChatStore((state) => state.clearActiveChatId);
    const { scheduleWrappingForTasks } = useWrapupTimer();

    /** activeChatTask memo derived directly from activeChatId and chatTasks */
    const activeChatTask = useMemo(
        () => chatTasks.find((task) => task.chatId === activeChatId),
        [activeChatId, chatTasks],
    );

    // Clear the activeChatId when no matching task is found
    useEffect(() => {
        if (activeChatId && !activeChatTask) {
            clearActiveChatId();
        }
    }, [activeChatId, activeChatTask, clearActiveChatId]);

    const onChatSelected = useCallback(
        (chat: ChatTask) => {
            if (chat.status === "pending") {
                logger.warn({ chatId: chat.chatId, taskId: chat.id }, "Attempted to select a pending chat");
                return;
            }

            const session = getSessionByTaskId(chat.id);
            if (session) {
                useSessionStore.getState().setSelectedSession(session.id);
            }
            setActiveChatId(chat.chatId);
        },
        [setActiveChatId],
    );

    const isSelectedChat = useCallback((chatTask: ChatTask) => activeChatId === chatTask.chatId, [activeChatId]);

    useEffect(() => {
        scheduleWrappingForTasks(chatTasks);
    }, [chatTasks, scheduleWrappingForTasks]);

    const { data: isSalesEnabled = false } = useFeatureFlag<boolean>("EWP-chat-isSalesEnabled", {
        partner: activeChatTask?.partner,
        topic: activeChatTask?.topic,
        language: activeChatTask?.language,
    });

    const { data: isChatExpertAssistEnabled = false, isLoading: isFeatureFlagLoading } = useChatExpertAssistEnabled({
        partner: activeChatTask?.partner,
        topic: activeChatTask?.topic,
    });

    const canShowChatDetails = activeChatTask && activeChatTask.status !== "pending";

    // TODO: CustomerPanel uses useTypedMostRecentTask which reads from session store so we need to ensure both are in sync
    const sessionForActiveChat = activeChatTask ? getSessionByTaskId(activeChatTask.id) : null;
    const shouldShowSalesPanel =
        activeChatTask && activeChatTask.status !== "pending" && isSalesEnabled && !!sessionForActiveChat?.currentTask;

    useSetupChatGaiaSubscriptions(logger);

    const shouldShowQueue = chatTasks.length > 0;

    return (
        <Panels
            lockMainPanelResize={true} // TODO: remove this when we can set min sizes for each side
            leftSidebarSlot={
                shouldShowQueue && (
                    <SlotPanel>
                        <ChatQueue chats={chatTasks} isSelectedChat={isSelectedChat} onChatSelected={onChatSelected} />
                    </SlotPanel>
                )
            }
            leftSidebarConfig={{
                minWidth: "13.75rem", // 220px
                maxWidth: "20rem", // 320px
                defaultSize: 15, // 15% instead of default 20%
                isCollapsed: !shouldShowQueue,
            }}
            topbarSlot={
                canShowChatDetails && (
                    <SlotPanel>
                        <ChatControlsBar chatTask={activeChatTask} />
                    </SlotPanel>
                )
            }
            topbarConfig={{
                isCollapsed: !canShowChatDetails,
                defaultHeight: 8, // Fixed height for chat controls
                flexGrow: 0,
                flexShrink: 0,
            }}
            rightSidebarSlot={
                shouldShowSalesPanel && (
                    <SlotPanel>
                        <CustomerPanel />
                    </SlotPanel>
                )
            }
            rightSidebarConfig={{
                isCollapsed: !shouldShowSalesPanel,
                minWidth: "18.75rem", // 300px
                maxWidth: "26.25rem", // 420px
                defaultSize: 25, // 25% instead of default 20%
            }}
            leftTopASlot={<ChatPanelTimelineView activeChatTask={activeChatTask} />}
            rightTopASlot={
                canShowChatDetails && (
                    <SlotPanel>
                        <PlatformTimeline />
                    </SlotPanel>
                )
            }
            leftTopConfig={{
                selectedTab: "a",
                a: { title: "Chat?", isMinimized: false, isClosed: false, showHeader: false },
                b: { title: "Not IN USE?", isMinimized: false, isClosed: true, showHeader: false },
            }}
            rightTopConfig={{
                selectedTab: "a",
                a: {
                    title: "EXPERT ASSIST",
                    isMinimized: false,
                    isClosed: !canShowChatDetails || !isChatExpertAssistEnabled || isFeatureFlagLoading,
                    showHeader: false,
                },
                b: { title: "Flows Someday", isMinimized: false, isClosed: true, showHeader: false },
            }}
        />
    );
}
